import { isEqual } from 'lodash';
import { reaction } from 'mobx';
import { FBFormState, FBMPIProcedureState, FBWorkspaceState } from '..';

/** Keep persistence attached to the step identity while its controls mount later. */
export const observeMPIProcedure = (
  name: string,
  mpiProcedureState: FBMPIProcedureState,
  workspaceState?: FBWorkspaceState,
  formState?: FBFormState,
): (() => void) => {
  const revisionId = workspaceState?.id;
  const disposers = [reaction(
    () => mpiProcedureState.value,
    (data) => {
      if (!workspaceState || workspaceState.id !== revisionId || !data) { return; }
      const index = workspaceState.schema?.findIndex(item => item.name === name && !item.deleted) ?? -1;
      const schemaItem = workspaceState.getSchemaItemAt(index);
      if (!schemaItem) { return; }
      const editorConfig = { ...schemaItem.editorConfig, ...data };
      if (isEqual(schemaItem.editorConfig, editorConfig)) { return; }
      workspaceState.setSchemaItemAt({ ...schemaItem, editorConfig }, index);
      workspaceState.scheduleDocRev(formState ? () => {
        formState.flushChangeValidation();
        return formState.getValues();
      } : undefined);
    },
  )];

  (['description', 'attachments'] as const).forEach((field) => {
    let registeredInput = formState?.inputState.get(`${name}.${field}`);
    disposers.push(reaction(
      () => {
        const input = formState?.inputState.get(`${name}.${field}`);
        return { input, value: input?.value };
      },
      ({ input, value }) => {
        if (workspaceState?.id !== revisionId) { return; }
        // Mounting/replacing an input is hydration; undefined is unregistration.
        if (input !== registeredInput) {
          registeredInput = input;
          return;
        }
        if (!input || value === undefined) { return; }
        const previousValue = mpiProcedureState.value?.[field];
        const unchanged = field === 'attachments'
          ? isEqual(value ?? [], previousValue ?? [])
          : isEqual(value, previousValue);
        if (unchanged) { return; }
        mpiProcedureState.setValue({ ...mpiProcedureState.value, [field]: value });
      },
    ));
  });

  return () => disposers.forEach(dispose => dispose());
};
